<template>
  <div class="rich-text-editor" :class="{ 'is-invalid': invalid }">
    <div v-if="editor" class="editor-toolbar">
      <!-- Undo / Redo -->
      <div class="toolbar-group">
        <button
          type="button"
          class="toolbar-btn"
          title="Undo"
          :disabled="!editor.can().undo()"
          @click="editor.chain().focus().undo().run()"
        >
          <i class="bi bi-arrow-counterclockwise"></i>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          title="Redo"
          :disabled="!editor.can().redo()"
          @click="editor.chain().focus().redo().run()"
        >
          <i class="bi bi-arrow-clockwise"></i>
        </button>
      </div>

      <div class="toolbar-divider"></div>

      <!-- Text formatting -->
      <div class="toolbar-group">
        <button
          type="button"
          class="toolbar-btn"
          title="Bold"
          :class="{ active: editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
        >
          <i class="bi bi-type-bold"></i>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          title="Italic"
          :class="{ active: editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
        >
          <i class="bi bi-type-italic"></i>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          title="Underline"
          :class="{ active: editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
        >
          <i class="bi bi-type-underline"></i>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          title="Strikethrough"
          :class="{ active: editor.isActive('strike') }"
          @click="editor.chain().focus().toggleStrike().run()"
        >
          <i class="bi bi-type-strikethrough"></i>
        </button>
      </div>

      <div class="toolbar-divider"></div>

      <!-- Lists -->
      <div class="toolbar-group">
        <button
          type="button"
          class="toolbar-btn"
          title="Bullet list"
          :class="{ active: editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
        >
          <i class="bi bi-list-ul"></i>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          title="Numbered list"
          :class="{ active: editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
        >
          <i class="bi bi-list-ol"></i>
        </button>
      </div>

      <div class="toolbar-divider"></div>

      <!-- Insert -->
      <div class="toolbar-group">
        <button
          type="button"
          class="toolbar-btn"
          title="Insert link"
          :class="{ active: editor.isActive('link') }"
          @click="setLink"
        >
          <i class="bi bi-link"></i>
        </button>
        <button
          type="button"
          class="toolbar-btn"
          title="Remove link"
          :disabled="!editor.isActive('link')"
          @click="editor.chain().focus().unsetLink().run()"
        >
          <i class="bi bi-link-45deg"></i>
        </button>
      </div>
    </div>

    <editor-content :editor="editor" class="editor-content rich-content" />
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import { looksLikeHtml, sanitizePastedHtml } from '@/utils/pastedHtml'

export default {
  name: 'RichTextEditor',
  components: {
    EditorContent
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    invalid: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      editor: null
    }
  },
  watch: {
    modelValue(value) {
      if (!this.editor) return
      // Avoid feedback loop: only reset content when it actually differs
      // from what the editor currently holds (e.g. external form reset).
      const isSame = value === this.editor.getHTML()
      if (!isSame) {
        this.editor.commands.setContent(value || '', false)
      }
    }
  },
  mounted() {
    this.editor = new Editor({
      content: this.modelValue || '',
      extensions: [
        StarterKit,
        Underline,
        Link.configure({
          openOnClick: false,
          autolink: true
        })
      ],
      editorProps: {
        // Pasted text that is HTML markup (copied from a source view, or pasted
        // with "paste and match style") is rendered rather than shown as tags.
        handlePaste: (view, event) => {
          const plain = event.clipboardData?.getData('text/plain')
          if (!looksLikeHtml(plain)) return false

          this.editor.commands.insertContent(sanitizePastedHtml(plain))
          return true
        }
      },
      onUpdate: () => {
        this.$emit('update:modelValue', this.editor.getHTML())
      }
    })
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy()
    }
  },
  methods: {
    setLink() {
      const previousUrl = this.editor.getAttributes('link').href
      const url = window.prompt('Enter URL (https://...)', previousUrl || '')

      if (url === null) return

      if (url === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }

      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }
  }
}
</script>

<style scoped>
.rich-text-editor {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
}

.rich-text-editor.is-invalid {
  border-color: #dc2626;
}

.editor-toolbar {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.toolbar-group {
  display: flex;
  gap: 0.125rem;
}

.toolbar-divider {
  width: 1px;
  height: 24px;
  background: #dee2e6;
  margin: 0 0.25rem;
}

.toolbar-btn {
  background: none;
  border: 1px solid transparent;
  padding: 0.375rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #495057;
  transition: all 0.15s ease;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toolbar-btn:hover {
  background: #e9ecef;
  border-color: #ced4da;
}

.toolbar-btn:active {
  background: #dee2e6;
}

.toolbar-btn.active {
  background: #dee2e6;
  border-color: #ced4da;
  color: #009cad;
}

.toolbar-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.editor-content {
  min-height: 150px;
  max-height: 400px;
  overflow-y: auto;
  padding: 1rem;
  background: white;
  line-height: 1.6;
  font-family: inherit;
  font-size: 1rem;
  color: #333;
}

.editor-content :deep(.ProseMirror) {
  outline: none;
  min-height: 120px;
}

.editor-content :deep(ul),
.editor-content :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
}
</style>
